<script setup>
import { computed, onMounted, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import api from 'dashboard/api/aceleres/iaRodizios';
import AutomationConfigurationForm from './AutomationConfigurationForm.vue';

const props = defineProps({
  teamId: { type: [Number, String], required: true },
  type: { type: String, required: true },
});

const emit = defineEmits(['changed']);

const DEFAULT_WORKING_HOURS = [0, 1, 2, 3, 4, 5, 6].map(day => ({
  day_of_week: day,
  closed_all_day: day === 0 || day === 6,
  open_hour: day === 0 || day === 6 ? null : 8,
  open_minutes: day === 0 || day === 6 ? null : 0,
  close_hour: day === 0 || day === 6 ? null : 19,
  close_minutes: day === 0 || day === 6 ? null : 0,
  open_all_day: false,
}));

const payload = ref({});
const expandedId = ref(null);
const draft = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);

const isAi = computed(() => props.type === 'ai');
const title = computed(() =>
  isAi.value
    ? 'Configurações de IA por instância'
    : 'Configurações de rodízio por instância'
);
const configurations = computed(() =>
  isAi.value
    ? payload.value.ai_configurations || []
    : payload.value.rodizio_configurations || []
);
const inboxes = computed(() => payload.value.inboxes || []);
const assistants = computed(() => payload.value.assistants || []);
const managers = computed(() => payload.value.managers || []);
const agents = computed(() => payload.value.agents || []);

const clone = value => JSON.parse(JSON.stringify(value));

const fetchConfigurations = async () => {
  isLoading.value = true;
  try {
    const { data } = await api.listAutomationConfigurations(props.teamId);
    payload.value = data;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Erro ao carregar configurações por instância'
    );
  } finally {
    isLoading.value = false;
  }
};

const defaultDraft = () => ({
  id: null,
  name: '',
  enabled: false,
  captain_assistant_id: null,
  inbox_ids: [],
  settings: {
    global_working_hours: clone(DEFAULT_WORKING_HOURS),
    rodizio_idle_minutes: 4,
    rodizio_reexhaust_wait_minutes: 4,
    rodizio_max_cycles: 1,
    rodizio_manager_user_id: null,
    rodizio_horario_mode: 'global',
    absence_alert_enabled: true,
    absence_alert_delay_minutes: 30,
    agent_working_hours: {},
  },
  selectedAgentId: null,
});

const openConfiguration = configuration => {
  if (expandedId.value === configuration.id) {
    expandedId.value = null;
    draft.value = null;
    return;
  }

  expandedId.value = configuration.id;
  draft.value = {
    ...defaultDraft(),
    ...clone(configuration),
    inbox_ids: [...configuration.inbox_ids],
    settings: {
      ...defaultDraft().settings,
      ...clone(configuration.settings || {}),
    },
  };
};

const createConfiguration = () => {
  expandedId.value = 'new';
  draft.value = defaultDraft();
};

const configurationIdForInbox = inbox =>
  isAi.value ? inbox.ai_configuration_id : inbox.rodizio_configuration_id;

const inboxDisabled = inbox => {
  const assigned = configurationIdForInbox(inbox);
  if (assigned && assigned !== draft.value?.id) return true;

  return !inbox.available_for_automation;
};

const inboxLabel = inbox => {
  const sharedTeams = isAi.value
    ? inbox.ai_shared_team_names || []
    : inbox.rodizio_shared_team_names || [];
  const details = [];

  if (
    inbox.owner_team_id &&
    String(inbox.owner_team_id) !== String(props.teamId)
  ) {
    details.push(
      `equipe proprietária: ${inbox.owner_team_name || 'outra equipe'}`
    );
  }
  if (sharedTeams.length) {
    details.push(`compartilhada com: ${sharedTeams.join(', ')}`);
  }
  if (!inbox.available_for_automation && inbox.unavailable_reason) {
    details.push(inbox.unavailable_reason);
  }

  return details.length ? `${inbox.name} — ${details.join(' • ')}` : inbox.name;
};

const selectedInboxNames = configuration =>
  configuration.inboxes?.map(inbox => inbox.name).join(', ') ||
  'Nenhuma instância';

const payloadForSave = () => ({
  automation_type: props.type,
  name: draft.value.name || null,
  enabled: draft.value.enabled,
  captain_assistant_id: isAi.value ? draft.value.captain_assistant_id : null,
  inbox_ids: draft.value.inbox_ids,
  ...draft.value.settings,
});

const save = async () => {
  if (!draft.value?.inbox_ids.length) {
    useAlert('Selecione ao menos uma instância');
    return;
  }

  isSaving.value = true;
  try {
    if (draft.value.id) {
      await api.updateAutomationConfiguration(
        props.teamId,
        draft.value.id,
        payloadForSave()
      );
    } else {
      await api.createAutomationConfiguration(props.teamId, payloadForSave());
    }
    useAlert('Configuração salva');
    expandedId.value = null;
    draft.value = null;
    await fetchConfigurations();
    emit('changed');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao salvar configuração');
  } finally {
    isSaving.value = false;
  }
};

const remove = async configuration => {
  if (
    !window.confirm(
      `Remover a configuração "${configuration.name || 'sem nome'}"?`
    )
  )
    return;

  try {
    await api.deleteAutomationConfiguration(props.teamId, configuration.id);
    useAlert('Configuração removida');
    if (expandedId.value === configuration.id) {
      expandedId.value = null;
      draft.value = null;
    }
    await fetchConfigurations();
    emit('changed');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao remover configuração');
  }
};

onMounted(fetchConfigurations);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <section class="flex flex-col gap-4">
    <header
      class="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h2 class="m-0 text-body-large font-medium text-n-slate-12">
          {{ title }}
        </h2>
        <p class="m-0 text-body-small text-n-slate-11">
          Um card pode atender várias instâncias. Para regras ou horários
          diferentes, crie outro card. Instâncias de outras equipes podem ser
          compartilhadas sem alterar a equipe proprietária. Apenas WhatsApps
          compartilhados ou APIs Oficiais configuradas podem ser selecionados.
        </p>
      </div>
      <Button
        label="Nova configuração"
        icon="i-lucide-plus"
        @click="createConfiguration"
      />
    </header>

    <p v-if="isLoading" class="m-0 text-body-small text-n-slate-11">
      Carregando configurações…
    </p>

    <div v-else class="flex flex-col gap-3">
      <article
        v-for="configuration in configurations"
        :key="configuration.id"
        class="overflow-hidden rounded-lg border border-n-strong bg-n-background"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between gap-3 px-4 py-4 text-left hover:bg-n-slate-2"
          @click="openConfiguration(configuration)"
        >
          <span class="min-w-0">
            <span
              class="block truncate text-body-medium font-medium text-n-slate-12"
            >
              {{ configuration.name || 'Configuração sem nome' }}
            </span>
            <span class="block truncate text-body-small text-n-slate-11">
              {{ selectedInboxNames(configuration) }}
            </span>
          </span>
          <span class="flex shrink-0 items-center gap-2">
            <span
              :class="
                configuration.enabled
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
              class="rounded px-2 py-1 text-label-small"
            >
              {{ configuration.enabled ? 'Ativa' : 'Desativada' }}
            </span>
            <span
              :class="
                expandedId === configuration.id
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
              class="size-4"
            />
          </span>
        </button>

        <div
          v-if="expandedId === configuration.id && draft"
          class="border-t border-n-weak p-4"
        >
          <AutomationConfigurationForm
            :draft="draft"
            :is-ai="isAi"
            :inboxes="inboxes"
            :assistants="assistants"
            :managers="managers"
            :agents="agents"
            :is-saving="isSaving"
            :inbox-disabled="inboxDisabled"
            :inbox-label="inboxLabel"
            @cancel="
              expandedId = null;
              draft = null;
            "
            @save="save"
          />
          <div class="flex justify-end">
            <Button
              label="Remover configuração"
              variant="ghost"
              class="text-n-ruby-9"
              @click="remove(configuration)"
            />
          </div>
        </div>
      </article>

      <article
        v-if="expandedId === 'new' && draft"
        class="rounded-lg border border-n-strong bg-n-background p-4"
      >
        <h3 class="m-0 text-body-medium font-medium text-n-slate-12">
          Nova configuração
        </h3>
        <AutomationConfigurationForm
          class="mt-4"
          :draft="draft"
          :is-ai="isAi"
          :inboxes="inboxes"
          :assistants="assistants"
          :managers="managers"
          :agents="agents"
          :is-saving="isSaving"
          :inbox-disabled="inboxDisabled"
          :inbox-label="inboxLabel"
          @cancel="
            expandedId = null;
            draft = null;
          "
          @save="save"
        />
      </article>

      <p
        v-if="!configurations.length && expandedId !== 'new'"
        class="m-0 rounded-lg border border-dashed border-n-strong px-4 py-8 text-center text-body-small text-n-slate-11"
      >
        Nenhuma configuração criada para esta equipe.
      </p>
    </div>
  </section>
</template>
